import { apiClient } from './apiClient';
import { CalendarEvent, Lead, SelfIPALinks } from '../types';

interface SalesCallResponse {
  id: number;
  startDate: string;
  endDate: string;
  type: 'VD' | 'VDFIELD';
  vdRoom: string | null;
  sellerName: string | null;
}

/**
 * Get lead data by email address.
 * Called after Firebase authentication to retrieve user's lead information.
 *
 * @param email - User's email address
 * @returns Lead data or null if not found
 */
export const getLead = async (): Promise<Lead | null> => {
  const response = await apiClient.get<Lead>(`/enpal-for-you/lead`);

  if (response.error) {
    console.error('Enpal-For-You getLeadByEmail failed:', response.error.message);
    return null;
  }

  return response.data ?? null;
};

/**
 * Get lead data by import ID.
 * Public endpoint - no authentication required.
 *
 * @param id - Lead's import ID
 * @returns Lead data or null if not found
 */
export const getLeadById = async (id: string): Promise<Lead | null> => {
  const response = await apiClient.get<Lead>(`/enpal-for-you/bonus-summary-data/${encodeURIComponent(id)}`);

  if (response.error) {
    console.error('Enpal-For-You getLeadById failed:', response.error.message);
    return null;
  }

  return response.data ?? null;
};

/**
 * Get the customer's upcoming sales call event.
 * Requires Firebase authentication (phone-based).
 *
 * @returns CalendarEvent or null if no upcoming sales call
 */
export const getSalesCall = async (): Promise<CalendarEvent | null> => {
  const response = await apiClient.get<SalesCallResponse>('/enpal-for-you/sales-call');

  if (response.error || !response.data) {
    return null;
  }

  const data = response.data;
  if (
    typeof data.startDate !== 'string' ||
    typeof data.endDate !== 'string' ||
    !data.startDate.trim() ||
    !data.endDate.trim()
  ) {
    return null;
  }

  const startDate = new Date(data.startDate);
  const endDate = new Date(data.endDate);
  if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime())) {
    return null;
  }

  const isFieldVisit = data.type === 'VDFIELD';

  return {
    id: data.id,
    title: isFieldVisit ? 'Visita Commerciale Enpal' : 'Chiamata Commerciale Enpal',
    description: isFieldVisit ? 'Visita con il tuo consulente Enpal' : 'Videochiamata con il tuo consulente Enpal',
    startDate,
    endDate,
    url: data.vdRoom ?? undefined,
    organizer: data.sellerName ? { name: data.sellerName, email: '' } : undefined,
  };
};

/**
 * Track add-to-calendar button clicks for an upcoming sales call.
 * Fire and forget - does not block UI or throw errors.
 */
export const trackCalendarEventClick = (eventId: number): void => {
  if (!Number.isInteger(eventId) || eventId <= 0) {
    return;
  }

  void apiClient.post<void>('/enpal-for-you/track-calendar-event', {
    eventId: String(eventId),
    timestamp: new Date().toISOString(),
  });
};

/**
 * Get self IPA links for the authenticated customer.
 *
 * @returns Self IPA links or null if unavailable
 */
export const getSelfIPALinks = async (): Promise<SelfIPALinks | null> => {
  const response = await apiClient.get<Partial<SelfIPALinks>>('/enpal-for-you/self-ipa');

  if (response.error || !response.data) {
    return null;
  }

  return {
    pv: typeof response.data.pv === 'string' ? response.data.pv.trim() : '',
    hp: typeof response.data.hp === 'string' ? response.data.hp.trim() : '',
  };
};

const TRACKING_ENDPOINT = 'https://enpal-salesos-prd-we-function.azurewebsites.net/api/track-install-app';
const BONUS_LEAD_ID_KEY = 'bonus_summary_lead_id';

export type EnpalForYouClientEngagementEventType =
  | 'pwa_install_cta_click'
  | 'pwa_install_dismiss'
  | 'pwa_install_native_prompt_result'
  | 'pwa_app_installed'
  | 'sms_magic_link_opened'
  | 'sms_verify_attempt';

/**
 * Universal Enpal For You engagement events (Sales OS Postgres).
 * Fire and forget — does not block UI or throw errors.
 * When `bonus_summary_lead_id` is in localStorage, it is sent as `context.lead_import_id` for subject enrichment.
 */
export const trackEnpalForYouEngagement = (payload: {
  type: EnpalForYouClientEngagementEventType;
  clickedAt?: string;
  context?: Record<string, unknown>;
}): void => {
  try {
    const merged: Record<string, unknown> = { ...(payload.context ?? {}) };
    if (typeof window !== 'undefined') {
      const leadImportId = localStorage.getItem(BONUS_LEAD_ID_KEY);
      if (leadImportId?.trim()) {
        merged.lead_import_id = leadImportId.trim();
      }
    }
    const body: { type: string; clickedAt: string; context?: Record<string, unknown> } = {
      type: payload.type,
      clickedAt: payload.clickedAt ?? new Date().toISOString(),
    };
    if (Object.keys(merged).length > 0) {
      body.context = merged;
    }
    void apiClient.post<void>('/enpal-for-you/track', body);
  } catch {
    // ignore
  }
};

/**
 * Track install app button clicks.
 * Fire and forget - does not block UI or throw errors.
 *
 * Phase 1: Still posts to the Azure Function in production so Mongo + EnpalForYouTrackingJob
 * continue to update `hasEnpalForYouApp` on leads. Phase 2: dual-write or migrate job to
 * Postgres engagement rows from POST /enpal-for-you/track.
 *
 * @param buttonClicked - The text of the clicked button
 */
export const trackInstallAppClick = (buttonClicked: string): void => {
  if (process.env.REACT_APP_ENV !== 'prd') return;

  const mongodbId = localStorage.getItem(BONUS_LEAD_ID_KEY) || '';

  fetch(TRACKING_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      button_clicked: buttonClicked,
      timestamp: new Date(),
      mongodb_id: mongodbId,
      user_agent: navigator.userAgent,
      platform: navigator.platform,
    }),
  }).catch(() => {
    // Silently ignore errors - fire and forget
  });
};

/** Structured address merged onto building/lead with the roof pin (matches backend DTO). */
export type RoofCoordinatesAddressDto = {
  streetName?: string;
  streetNumber?: string;
  zip?: string;
  city?: string;
  province?: string;
  state?: string;
  region?: string;
  country?: string;
};

const ROOF_ADDRESS_MAX: Record<keyof RoofCoordinatesAddressDto, number> = {
  streetName: 255,
  streetNumber: 32,
  zip: 16,
  city: 128,
  province: 64,
  state: 128,
  region: 128,
  country: 128,
};

function trimRoofAddressField(key: keyof RoofCoordinatesAddressDto, value: string): string | undefined {
  const t = value.trim();
  if (t === '') {
    return undefined;
  }
  const max = ROOF_ADDRESS_MAX[key];
  return t.length <= max ? t : t.slice(0, max);
}

export type GeocodeResultItem = {
  lat: number;
  lng: number;
  formattedAddress: string;
  placeId: string;
} & Partial<RoofCoordinatesAddressDto>;

/** Builds the PUT body fragment from geocode results when the API returns structured components. */
export function roofAddressDtoFromGeocodeResult(r: GeocodeResultItem): RoofCoordinatesAddressDto {
  const dto: RoofCoordinatesAddressDto = {};
  (Object.keys(ROOF_ADDRESS_MAX) as (keyof RoofCoordinatesAddressDto)[]).forEach((k) => {
    const raw = r[k];
    if (raw == null) {
      return;
    }
    const trimmed = trimRoofAddressField(k, String(raw));
    if (trimmed !== undefined) {
      dto[k] = trimmed;
    }
  });
  return dto;
}

export function hasRoofAddressDtoValues(d: RoofCoordinatesAddressDto): boolean {
  return (Object.keys(ROOF_ADDRESS_MAX) as (keyof RoofCoordinatesAddressDto)[]).some(
    (k) => d[k] != null && String(d[k]).trim() !== ''
  );
}

/**
 * Maps JavaScript API key for the current user (served from backend, same as GOOGLE_MAPS_API_KEY).
 * Requires REACT_APP_API_URL and a signed-in Firebase user.
 */
export const getGoogleMapsApiKey = async (): Promise<string | null> => {
  const response = await apiClient.get<{ apiKey: string }>('/enpal-for-you/maps-api-key');
  if (response.error) {
    console.error('enpal-for-you maps-api-key failed:', response.error.message);
    return null;
  }
  const key = response.data?.apiKey;
  if (typeof key === 'string' && key.trim() !== '') {
    return key;
  }
  return null;
};

/**
 * Persists customer roof pin: same building fields as Sales OS (custCoordX=lng, custCoordY=lat).
 * Optional structured address (from geocode / Places) is merged onto building with the pin.
 *
 * Server contract: `PUT /enpal-for-you/lead/roof-coordinates` should still return success and
 * persist lat/lng on the lead when no building can be resolved, instead of failing the whole request.
 */
export const saveRoofCoordinates = async (
  lat: number,
  lng: number,
  address?: RoofCoordinatesAddressDto | null
): Promise<{ ok: boolean; error: string | null }> => {
  const payload: { lat: number; lng: number } & RoofCoordinatesAddressDto = { lat, lng };
  if (address) {
    (Object.keys(ROOF_ADDRESS_MAX) as (keyof RoofCoordinatesAddressDto)[]).forEach((k) => {
      const v = address[k];
      if (v == null) {
        return;
      }
      const trimmed = trimRoofAddressField(k, String(v));
      if (trimmed !== undefined) {
        payload[k] = trimmed;
      }
    });
  }
  const response = await apiClient.put<{ ok: true }>('/enpal-for-you/lead/roof-coordinates', payload);
  if (response.error) {
    return { ok: false, error: response.error.message };
  }
  if (response.data?.ok) {
    return { ok: true, error: null };
  }
  return { ok: true, error: null };
};

/**
 * Address search via server-side Geocoding (no Geocoder in the browser).
 */
export const geocodeAddressForEnpalForYou = async (address: string): Promise<GeocodeResultItem[] | null> => {
  const trimmed = address.trim();
  if (trimmed === '') {
    return null;
  }
  const response = await apiClient.post<{ results: GeocodeResultItem[] }>('/enpal-for-you/geocode', {
    address: trimmed,
  });
  if (response.error) {
    return null;
  }
  return response.data?.results ?? null;
};
